<template>
  <div class="video-page-container">
    <!-- 顶部导航栏 -->
    <div class="top-bar">
      <!-- 左侧菜单按钮 (打开抽屉) -->
      <div class="menu-btn" @click.stop="toggleDrawer">
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
      </div>

      <!-- 可滚动的分类列表 -->
      <div class="category-scroll-wrapper" ref="categoryWrapper">
        <div 
          class="category-item" 
          :class="{ active: currentCategory === cat.value }"
          v-for="cat in categoryList" 
          :key="cat.value"
          @click.stop="switchCategory(cat.value)"
        >
          {{ cat.label }}
        </div>
      </div>
      
      <!-- 右上角静音按钮 -->
      <div class="mute-btn" @click.stop="toggleGlobalMute">
        <svg v-if="isMuted" viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
        <svg v-else viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
      </div>
    </div>

    <!-- 视频滚动容器 -->
    <div 
      class="video-scroll-container" 
      ref="scrollContainer"
      @scroll="handleScroll"
    >
      <div 
        class="video-item" 
        v-for="(product, index) in productList" 
        :key="product.id"
        :id="'video-item-' + index"
      >
        <!-- 视频层 -->
        <video
          ref="videoRefs"
          class="bg-video"
          :src="getProxyVideoUrl(product.videoUrl)"
          loop
          playsinline
          webkit-playsinline
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          preload="metadata"
          :muted="isMuted"
          crossorigin="anonymous"
          @timeupdate="(e) => onTimeUpdate(e, index)"
          @loadedmetadata="(e) => onLoadedMetadata(e, index)"
          @error="() => handleVideoError(product.id)"
          @waiting="() => handleVideoWaiting(product.id)"
          @playing="() => handleVideoPlaying(product.id)"
          @pause="() => handleVideoPause(index)"
        ></video>

        <!-- 封面图覆盖层 (暂停时显示，且非滚动状态) -->
        <!-- 增加 key 强制重绘，防止状态不同步 -->
        <div 
          v-if="!isCurrentPlaying(index) && product.coverImage && !isScrolling" 
          class="cover-overlay"
          @click.stop="handleVideoClick(index)"
        >
          <img :src="product.coverImage" alt="cover" class="cover-image" />
          <div class="play-icon-overlay">
            <svg viewBox="0 0 24 24" width="60" height="60" fill="rgba(255,255,255,0.8)"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>

        <!-- 点击区域 (用于暂停/播放，位于封面之下，但在其他UI之上) -->
         <div 
          v-if="isCurrentPlaying(index)" 
          class="click-layer" 
          @click.stop="handleVideoClick(index)"
        ></div>
        
        <!-- 视频加载中/缓冲提示层 -->
        <div v-if="videoStatus[product.id] === 'loading' || videoStatus[product.id] === 'buffering'" class="video-status-overlay">
          <div class="loading-spinner"></div>
          <span class="status-text">{{ videoStatus[product.id] === 'loading' ? '加载中...' : '缓冲中...' }}</span>
        </div>

        <!-- 视频加载失败提示层 -->
        <div v-if="videoStatus[product.id] === 'error'" class="video-error-overlay">
          <div class="error-content">
            <svg class="error-icon" viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>视频暂无法播放</p>
          </div>
        </div>

        <!-- 遮罩层 -->
        <div class="overlay"></div>

        <!-- 左下角信息区域 -->
        <div class="info-area">
          <h3 class="product-name">@{{ product.name }}</h3>
          <div class="info-row">
            <span class="label">规格:</span> {{ product.specification }}
          </div>
          <div class="info-row">
            <span class="label">单价:</span> ¥{{ product.unitPrice }} / {{ product.unit }}
          </div>
          <div class="info-row">
            <span class="label">印刷:</span> {{ product.packagePrintTech }}
          </div>
          <div class="info-row">
            <span class="label">外包装:</span> {{ product.outerPackageSize }} cm
          </div>
        </div>

        <!-- 右侧操作区 -->
     <div class="right-actions">
          <!-- 心愿单按钮 (样式已统一为圆形透明背景) -->
          <div 
            class="action-btn wishlist-btn" 
            :class="{ 'animate-like': isInWishlist(product.id) && justAddedId === product.id }"
            @click.stop="handleWishlistClick(product, $event)"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" :fill="isInWishlist(product.id) ? '#ff4d4f' : 'none'" :stroke="isInWishlist(product.id) ? '#ff4d4f' : '#fff'" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </div>

          <!-- 倍速球 -->
          <div class="speed-ball" @click.stop="toggleSpeed(product.id)">
            {{ speedMap[product.id] === 2.0 ? '2.0x' : '1.0x' }}
          </div>
        </div>

        <!-- 右下角视频序号 -->
        <div class="video-counter">
          {{ String(index + 1).padStart(2, '0') }} / {{ totalNum }}
        </div>

        <!-- 底部进度条控制区 -->
        <div class="progress-control-area">
          <div class="progress-bar-container" @click.stop="seekVideo">
            <div class="progress-bg"></div>
            <div class="progress-current" :style="{ width: getProgressPercent(index) + '%' }"></div>
            <div class="progress-thumb" :style="{ left: getProgressPercent(index) + '%' }"></div>
          </div>
          <div class="time-display">
            <span>{{ formatTime(currentTimes[index] || 0) }}</span>
            <span>/</span>
            <span>{{ formatTime(durations[index] || 0) }}</span>
          </div>
        </div>
        
        <div v-if="index === productList.length - 1 && noMore" class="no-more-tip">
          没有下一个了
        </div>
      </div>
      
      <div class="bottom-spacer"></div>
    </div>
    
    <!-- 全局初始加载 -->
    <div v-if="loading && productList.length === 0" class="global-loading-mask">
      <div class="loading-spinner-large"></div>
      <p>正在获取产品...</p>
    </div>

    <!-- 左侧抽屉 (意向产品) -->
    <transition name="drawer-slide">
      <div v-if="isDrawerOpen" class="drawer-mask" @click.stop="closeDrawer"></div>
    </transition>
    <transition name="drawer-slide">
      <div v-if="isDrawerOpen" class="drawer-container">
        <div class="drawer-header">
          <h3>我的意向产品</h3>
          <span class="close-drawer" @click="closeDrawer">×</span>
        </div>
        
        <div class="drawer-content">
          <div v-if="wishlist.length === 0" class="empty-wishlist">
            暂无意向产品
          </div>
          <div 
            v-for="item in wishlist" 
            :key="item.id" 
            class="wishlist-item"
            @click="jumpToProduct(item.id)"
          >
            <div class="item-info">
              <div class="item-name">{{ item.name }}</div>
              <div class="item-spec">{{ item.specification }}</div>
            </div>
            <div class="item-delete" @click.stop="removeFromWishlist(item.id)">
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </div>
          </div>
        </div>

        <!-- 意向订单提交区域 -->
        <div class="drawer-footer">
          <div class="input-group">
            <input 
              type="tel" 
              v-model="intentPhone" 
              placeholder="请输入手机号码" 
              class="phone-input"
              maxlength="11"
            />
            <div class="action-buttons">
              <button 
                class="submit-btn" 
                @click="submitIntentOrder" 
                :disabled="!isValidPhone || isSubmitting || wishlist.length === 0"
              >
                {{ isSubmitting ? '提交中...' : '提交' }}
              </button>
              <button 
                class="clear-btn" 
                @click="clearWishlist"
                :disabled="isSubmitting || wishlist.length === 0"
                title="清空心愿单"
              >
                清空
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 自定义 Toast 提示 -->
    <transition name="toast-fade">
      <div v-if="toast.visible" class="toast-container" :class="toast.type">
        {{ toast.message }}
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, reactive, computed } from 'vue';
import { BASE_URL } from '@/config'; 

// --- 状态定义 ---
const categoryList = ref([{ label: '全部', value: '' }]);
const productList = ref([]);
const currentCategory = ref('');
const loading = ref(false);
const pageIndex = ref(1);
const pageSize = ref(20);
const totalPage = ref(1);
const totalNum = ref(0);
const noMore = ref(false);
const scrollContainer = ref(null);
const videoRefs = ref([]);

const isMuted = ref(false); 
const videoStatus = reactive({}); 
const speedMap = reactive({}); 
const currentTimes = reactive({});
const durations = reactive({});

// 新增状态
const isDrawerOpen = ref(false);
const wishlist = ref([]); 
const playingIndex = ref(-1); 
const isScrolling = ref(false); 

// 意向订单相关状态
const intentPhone = ref('');
const isSubmitting = ref(false);
const justAddedId = ref(null); // 用于触发动画

// Toast 提示状态
const toast = reactive({
  visible: false,
  message: '',
  type: 'success', // 'success' | 'error'
  timer: null
});

// --- 初始化心愿单 ---
const initWishlist = () => {
  const stored = localStorage.getItem('jp_wishlist');
  if (stored) {
    try {
      wishlist.value = JSON.parse(stored);
    } catch (e) {
      console.error("Parse wishlist error", e);
      wishlist.value = [];
    }
  }
};

const saveWishlist = () => {
  localStorage.setItem('jp_wishlist', JSON.stringify(wishlist.value));
};

// --- 辅助函数 ---
const getProxyVideoUrl = (originalUrl) => {
  if (!originalUrl) return '';
  const encodedUrl = encodeURIComponent(originalUrl);
  return `${BASE_URL}/ClientApi/ProxyVideo?url=${encodedUrl}`;
};

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getProgressPercent = (index) => {
  const duration = durations[index];
  const current = currentTimes[index];
  if (!duration || duration === 0) return 0;
  return (current / duration) * 100;
};

const isCurrentPlaying = (index) => {
  return playingIndex.value === index;
};

const isInWishlist = (productId) => {
  return wishlist.value.some(item => item.id === productId);
};

// 验证手机号
const isValidPhone = computed(() => {
  return /^1[3-9]\d{9}$/.test(intentPhone.value);
});

// --- Toast 提示逻辑 ---
const showToast = (message, type = 'success', duration = 2500) => {
  if (toast.timer) clearTimeout(toast.timer);
  toast.message = message;
  toast.type = type;
  toast.visible = true;
  
  toast.timer = setTimeout(() => {
    toast.visible = false;
  }, duration);
};

// --- API 请求 ---
const fetchProducts = async (isRefresh = false) => {
  if (loading.value) return;
  loading.value = true;

  try {
    const url = `${BASE_URL}/ClientApi/GetProducts`;
    const params = new URLSearchParams();
    params.append('pageIndex', isRefresh ? 1 : pageIndex.value);
    params.append('pageSize', pageSize.value);
    if (currentCategory.value) {
      params.append('Category', currentCategory.value);
    }

    const response = await fetch(`${url}?${params.toString()}`);
    const res = await response.json();

    if (res.code === 200) {
      const data = res.data;
      
      if (isRefresh && data.categories && data.categories.length > 0) {
        const extraCats = data.categories.map(c => ({ label: c, value: c }));
        const existingValues = new Set(categoryList.value.map(c => c.value));
        extraCats.forEach(c => {
          if (!existingValues.has(c.value)) {
            categoryList.value.push(c);
          }
        });
      }

      const newProducts = data.products.result || [];
      totalNum.value = data.products.totalNum || 0;
      
      if (isRefresh) {
        productList.value = newProducts;
        pageIndex.value = 1;
        Object.keys(currentTimes).forEach(k => delete currentTimes[k]);
        Object.keys(durations).forEach(k => delete durations[k]);
        playingIndex.value = -1; 
        
        await nextTick();
        if (scrollContainer.value) {
          scrollContainer.value.scrollTop = 0;
        }
      } else {
        productList.value = [...productList.value, ...newProducts];
      }

      totalPage.value = data.products.totalPage;
      noMore.value = pageIndex.value >= totalPage.value;

      newProducts.forEach(p => {
        if (!speedMap[p.id]) speedMap[p.id] = 1.0;
        if (!videoStatus[p.id]) videoStatus[p.id] = 'loading';
      });

      if (isRefresh) {
        playVideoByIndex(0);
      }
    } else {
      console.error('API Error:', res.msg);
    }
  } catch (error) {
    console.error('Request Failed:', error);
  } finally {
    loading.value = false;
  }
};

// --- 交互逻辑 ---

const switchCategory = (catValue) => {
  if (currentCategory.value === catValue) return;
  currentCategory.value = catValue;
  pageIndex.value = 1;
  noMore.value = false;
  fetchProducts(true); 
};

const toggleGlobalMute = () => {
  isMuted.value = !isMuted.value;
  const videos = document.querySelectorAll('.bg-video');
  videos.forEach(v => {
    v.muted = isMuted.value;
  });
};

const toggleSpeed = (productId) => {
  const index = productList.value.findIndex(p => p.id === productId);
  if (index === -1) return;
  const videoEl = videoRefs.value[index];
  if (videoEl) {
    const currentSpeed = speedMap[productId] || 1.0;
    const newSpeed = currentSpeed === 1.0 ? 2.0 : 1.0;
    try {
      videoEl.playbackRate = newSpeed;
      speedMap[productId] = newSpeed;
    } catch (e) {
      console.warn("Set playback rate failed", e);
    }
  }
};

// 播放控制 - 支持双击收藏（延迟执行单击）
let clickTimer = null;
const DOUBLE_CLICK_THRESHOLD = 300; // 毫秒

const handleVideoClick = (index) => {
  // 如果已经有定时器在运行，说明这是第二次点击（双击）
  if (clickTimer) {
    clearTimeout(clickTimer);
    clickTimer = null;
    
    // 执行双击逻辑：收藏
    const product = productList.value[index];
    if (product) {
      handleWishlistClick(product, null); // 手动触发收藏逻辑
    }
  } else {
    // 第一次点击，设置定时器等待是否会有第二次点击
    clickTimer = setTimeout(() => {
      // 300ms 后没有第二次点击，执行单击逻辑：播放/暂停
      togglePlayByClick(index);
      clickTimer = null;
    }, DOUBLE_CLICK_THRESHOLD);
  }
};

const togglePlayByClick = (index) => {
  const videoEl = videoRefs.value[index];
  if (!videoEl) return;

  if (videoEl.paused) {
    videoEl.play().catch(e => console.warn("Play failed", e));
  } else {
    videoEl.pause();
  }
};

const handleVideoPause = (index) => {
  if (playingIndex.value === index) {
    playingIndex.value = -1; 
  }
};

const handleVideoPlaying = (productId) => {
  const index = productList.value.findIndex(p => p.id === productId);
  if (index !== -1) {
    playingIndex.value = index;
    videoStatus[productId] = 'playing';
  }
};

// 心愿单逻辑（带动画触发）
const handleWishlistClick = (product) => {
  toggleWishlist(product);
  
  // 如果添加了收藏，触发动画
  if (isInWishlist(product.id)) {
    justAddedId.value = product.id;
    setTimeout(() => {
      justAddedId.value = null;
    }, 400);
  }
};

const toggleWishlist = (product) => {
  const index = wishlist.value.findIndex(item => item.id === product.id);
  if (index === -1) {
    wishlist.value.push({
      id: product.id,
      name: product.name,
      specification: product.specification,
      coverImage: product.coverImage,
      videoUrl: product.videoUrl
    });
  } else {
    wishlist.value.splice(index, 1);
  }
  saveWishlist();
};

const removeFromWishlist = (productId) => {
  const index = wishlist.value.findIndex(item => item.id === productId);
  if (index !== -1) {
    wishlist.value.splice(index, 1);
    saveWishlist();
  }
};

// 清空整个心愿单
const clearWishlist = () => {
  if (wishlist.value.length === 0) return;
  wishlist.value = [];
  saveWishlist();
  showToast('心愿单已清空', 'success', 1500);
};

// 提交意向订单
const submitIntentOrder = async () => {
  if (!isValidPhone.value) {
    showToast('请输入有效的手机号码', 'error');
    return;
  }
  
  if (wishlist.value.length === 0) {
    showToast('请先添加意向产品', 'error');
    return;
  }

  isSubmitting.value = true;

  try {
    // 构造参数
    const items = wishlist.value.map(item => ({
      productId: item.id,
      productName: item.name,
      specification: item.specification
    }));

    const payload = {
      phone: intentPhone.value,
      items: items
    };

    const response = await fetch(`${BASE_URL}/ClientApi/CreateIntentOrder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const res = await response.json();

    if (res.code === 200) {
      showToast('提交成功！专属VIP业务将尽快联系您。', 'success');
      closeDrawer();
      intentPhone.value = '';
    } else {
      showToast('提交失败: ' + (res.msg || '未知错误'), 'error');
    }
  } catch (error) {
    console.error('Submit Intent Order Failed:', error);
    showToast('网络请求失败，请稍后重试', 'error');
  } finally {
    isSubmitting.value = false;
  }
};

// 抽屉逻辑
const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

const closeDrawer = () => {
  isDrawerOpen.value = false;
};

const jumpToProduct = (productId) => {
  const index = productList.value.findIndex(p => p.id === productId);
  if (index !== -1) {
    closeDrawer();
    const container = scrollContainer.value;
    const targetScroll = index * container.clientHeight;
    container.scrollTo({ top: targetScroll, behavior: 'smooth' });
    setTimeout(() => {
      playVideoByIndex(index);
    }, 500);
  } else {
    showToast("该产品不在当前加载列表中", "error");
  }
};

// 视频事件
const onTimeUpdate = (e, index) => {
  currentTimes[index] = e.target.currentTime;
};

const onLoadedMetadata = (e, index) => {
  durations[index] = e.target.duration;
  const pid = productList.value[index]?.id;
  if (pid && videoStatus[pid] === 'loading') {
    videoStatus[pid] = 'paused';
  }
};

const handleVideoError = (productId) => {
  videoStatus[productId] = 'error';
};

const handleVideoWaiting = (productId) => {
  if (videoStatus[productId] !== 'error') {
    videoStatus[productId] = 'buffering';
  }
};

const seekVideo = (e) => {
  const currentIndex = playingIndex.value !== -1 ? playingIndex.value : Math.round(scrollContainer.value.scrollTop / scrollContainer.value.clientHeight);
  const videoEl = videoRefs.value[currentIndex];
  
  if (videoEl && durations[currentIndex]) {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const seekTime = pos * durations[currentIndex];
    try {
      videoEl.currentTime = seekTime;
    } catch (err) {
      console.warn("Seek failed", err);
    }
  }
};

// 滚动监听优化 - 重点修复封面闪烁
let scrollTimer = null;
const handleScroll = () => {
  if (!scrollContainer.value) return;
  
  // 1. 立即标记为滚动中，确保封面瞬间隐藏
  isScrolling.value = true;

  // 2. 清除之前的定时器
  if (scrollTimer) clearTimeout(scrollTimer);
  
  // 3. 设置新的定时器，只有当滚动停止 50ms 后才认为滚动结束
  scrollTimer = setTimeout(() => {
    scrollTimer = null;
    // 滚动停止
    isScrolling.value = false;

    const container = scrollContainer.value;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const currentIndex = Math.round(scrollTop / clientHeight);

    if (currentIndex < 0 || currentIndex >= productList.value.length) return;

    if (currentIndex === productList.value.length - 1 && !noMore.value && !loading.value) {
       if (scrollTop + clientHeight >= container.scrollHeight - 50) {
         pageIndex.value++;
         fetchProducts();
       }
    }

    playVideoByIndex(currentIndex);
  }, 50); 
};

const playVideoByIndex = (index) => {
  const videos = videoRefs.value;
  
  videos.forEach((video, i) => {
    const productId = productList.value[i]?.id;
    if (!productId) return;

    if (i === index) {
      if (videoStatus[productId] === 'error') return;
      if (video.readyState < 3) videoStatus[productId] = 'loading';

      if (video.paused) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name !== 'NotAllowedError' && !error.message.includes('interrupted')) {
              console.warn(`Play failed for index ${i}:`, error.message);
            }
          });
        }
      }
    } else {
      if (!video.paused) {
        try { 
          video.pause(); 
        } catch (e) {
          // Ignore pause error
        }
      }
      if (videoStatus[productId] === 'loading' || videoStatus[productId] === 'buffering') {
         videoStatus[productId] = 'paused';
      }
    }
  });
};

onMounted(() => {
  initWishlist();
  fetchProducts(true);
});
</script>

<style scoped>
/* 全局容器 */
.video-page-container {
  width: 100vw;
  height: 100vh;
  background-color: #000;
  position: relative;
  overflow: hidden;
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* 顶部导航 */
.top-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 50px;
  z-index: 20;
  display: flex;
  align-items: center;
  padding: 0 10px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.6), transparent);
}

.menu-btn {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 22;
  color: #fff;
  flex-shrink: 0;
  margin-right: 5px;
}

.category-scroll-wrapper {
  flex: 1;
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-left: 5px;
}
.category-scroll-wrapper::-webkit-scrollbar { display: none; }

.category-item {
  display: inline-block;
  padding: 5px 12px;
  font-size: 15px;
  opacity: 0.8;
  transition: all 0.2s;
  cursor: pointer;
  color: #ddd;
}
.category-item.active { opacity: 1; font-weight: bold; color: #fff; border-bottom: 2px solid #fff; }

.mute-btn {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 21;
  color: #fff;
  flex-shrink: 0;
  margin-right: 8px; /* 修改：右边距 8px */
}

/* 滚动容器 */
.video-scroll-container {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
}

.video-item {
  width: 100%;
  height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.bg-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 封面覆盖层 */
.cover-overlay {
    transition: opacity 0.2s ease; /* 加快过渡 */
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 4;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.3); 
  pointer-events: auto; 
}

.cover-image {
  max-width: 90%; 
  max-height: 90%; 
  width: auto;
  height: auto;
  object-fit: contain; 
  border-radius: 8px; 
  box-shadow: 0 4px 12px rgba(0,0,0,0.5); 
}

.play-icon-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5)); 
}

/* 点击层 (用于播放/暂停) */
.click-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
}

/* 状态覆盖层 */
.video-status-overlay, .video-error-overlay {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.4);
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.video-error-overlay { background-color: rgba(0,0,0,0.7); }
.loading-spinner {
  width: 30px; height: 30px;
  border: 3px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 10px;
}
.loading-spinner-large {
  width: 50px; height: 50px;
  border: 4px solid rgba(255,255,255,0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}
@keyframes spin { to { transform: rotate(360deg); } }
.status-text { font-size: 14px; color: #fff; }
.error-content { text-align: center; color: #ccc; }
.error-icon { color: #ff4d4f; margin-bottom: 10px; }
 
/* 遮罩 */
.overlay {
  position: absolute;
  bottom: 0; left: 0; width: 100%; height: 60%;
  background: linear-gradient(to top, rgba(0,0,0,0.9), transparent);
  pointer-events: none;
  z-index: 1;
}

/* 信息区域 */
.info-area {
  position: absolute;
  bottom: 70px;
  left: 15px;
  right: 80px;
  z-index: 5;
  text-align: left;
}
.product-name { font-size: 18px; font-weight: bold; margin-bottom: 8px; text-shadow: 1px 1px 2px rgba(0,0,0,0.8); }
.info-row { font-size: 13px; margin-bottom: 3px; opacity: 0.9; text-shadow: 1px 1px 1px rgba(0,0,0,0.8); line-height: 1.4; }
.label { opacity: 0.7; margin-right: 5px; }

/* 右侧操作区 - 确保布局正确 */
.right-actions {
  position: absolute;
  right: 10px;
  bottom: 95px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 统一心愿单和倍速球的样式 */
.action-btn {
  margin-bottom: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  width: 40px; 
  height: 40px; 
  border-radius: 50%; 
  background: rgba(255, 255, 255, 0.2); 
  backdrop-filter: blur(4px); 
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
}

.action-btn:active {
  transform: scale(0.9);
  background: rgba(255, 255, 255, 0.4);
}

/* 心形动画类 */
.animate-like {
  animation: like-bounce 0.4s ease-in-out;
}

@keyframes like-bounce {
  0% { transform: scale(1); }
  25% { transform: scale(1.3); }
  50% { transform: scale(0.9); }
  75% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.wishlist-btn svg {
  width: 22px; 
  height: 22px;
  transition: fill 0.3s ease, stroke 0.3s ease;
}

.speed-ball {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
}
/* 视频序号 */
.video-counter {
  position: absolute;
  right: 15px;
  bottom: 65px;
  z-index: 5;
  font-size: 14px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
}

/* 进度控制区 */
.progress-control-area {
  position: absolute;
  bottom: 0; left: 0; width: 100%; height: 60px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: 15px;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
}
.progress-bar-container {
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 5px;
  box-sizing: border-box;
  margin-bottom: 5px;
}
.progress-bg { position: absolute; width: 100%; height: 2px; background: rgba(255,255,255,0.3); border-radius: 1px; }
.progress-current { position: absolute; height: 2px; background: #fff; border-radius: 1px; width: 0; }
.progress-thumb {
  position: absolute;
  width: 10px; height: 10px;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
  opacity: 0; 
  transition: opacity 0.2s;
}
.progress-bar-container:hover .progress-thumb { opacity: 1; }
.time-display {
  position: absolute;
  right: 10px;
  bottom: 38px;
  font-size: 12px;
  color: #fff;
  text-shadow: 1px 1px 2px rgba(0,0,0,1);
  pointer-events: none;
  background: rgba(0,0,0,0.3);
  padding: 2px 6px;
  border-radius: 4px;
}

.no-more-tip {
  position: absolute;
  bottom: 80px;
  width: 100%;
  text-align: center;
  font-size: 13px;
  color: rgba(255,255,255,0.4);
  z-index: 5;
}
.bottom-spacer { height: 1px; }
.global-loading-mask {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: #000;
  z-index: 100;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #fff;
}

/* 抽屉样式 */
.drawer-mask {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 40;
}
.drawer-container {
  position: absolute;
  top: 0; left: 0;
  width: 280px;
  height: 100%;
  background: #1a1a1a;
  z-index: 41;
  box-shadow: 2px 0 10px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
}
.drawer-header {
  height: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px;
  border-bottom: 1px solid #333;
  color: #fff;
}
.close-drawer { font-size: 24px; cursor: pointer; }
.drawer-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;
}
.empty-wishlist {
  text-align: center;
  color: #666;
  margin-top: 50px;
}
.wishlist-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  border-bottom: 1px solid #333;
  cursor: pointer;
  transition: background 0.2s;
}
.wishlist-item:hover { background: #2a2a2a; }
.item-info { flex: 1; overflow: hidden; }
.item-name { font-size: 14px; color: #fff; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.item-spec { font-size: 12px; color: #999; }
.item-delete {
  padding: 5px;
  color: #666;
  cursor: pointer;
}
.item-delete:hover { color: #ff4d4f; }

/* 抽屉底部表单样式 - 优化版 */
.drawer-footer {
  padding: 8px;
  border-top: 1px solid #333;
  background: #1a1a1a;
  width: 100%;
  box-sizing: border-box; /* 关键：防止padding导致宽度溢出 */
}

.input-group {
  display: flex;
  gap: 8px;
  width: 100%;
  align-items: center;
}

.phone-input {
  flex: 1; /* 自动占据剩余空间 */
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #444;
  background: #2a2a2a;
  color: #fff;
  font-size: 14px;
  outline: none;
  min-width: 0; /* 防止flex子项内容溢出 */
  box-sizing: border-box;
}

.phone-input:focus {
  border-color: #ff4d4f;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0; /* 防止按钮被压缩 */
}

.submit-btn {
  padding: 8px 12px;
  border-radius: 4px;
  border: none;
  background: #ff4d4f;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  min-width: 60px;
}

.submit-btn:hover:not(:disabled) {
  background: #ff7875;
}

.submit-btn:disabled {
  background: #555;
  cursor: not-allowed;
  opacity: 0.7;
}

.clear-btn {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #555;
  background: transparent;
  color: #ccc;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 60px;
}

.clear-btn:hover:not(:disabled) {
  border-color: #888;
  color: #fff;
  background: rgba(255,255,255,0.05);
}

.clear-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Toast 提示样式 */
.toast-container {
  position: fixed;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  backdrop-filter: blur(4px);
  max-width: 80%;
  text-align: center;
  pointer-events: none; /* 防止阻挡点击 */
}

.toast-container.error {
  background-color: rgba(45, 45, 45, 0.95);
  border: 1px solid #ff4d4f;
  color: #ffccc7;
}

/* Toast 动画 */
.toast-fade-enter-active, .toast-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.toast-fade-enter-from, .toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}
.toast-fade-enter-to, .toast-fade-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* 抽屉动画 */
.drawer-slide-enter-active, .drawer-slide-leave-active {
  transition: all 0.3s ease;
}
.drawer-slide-enter-from, .drawer-slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
.drawer-slide-enter-to, .drawer-slide-leave-from {
  transform: translateX(0);
  opacity: 1;
}
</style>